import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/lib/useBrand";
import { Button } from "@/components/ui/button";
import { Lightning, CaretDown } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout, appConfig } = useAuth();
  const { name: appName, logoUrl, subtitle, initial } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const showPricing = appConfig?.show_pricing ?? false;

  // Hide global navbar inside the portal-embed iframe — the portal owns the chrome.
  if (location.pathname.startsWith("/embed")) return null;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const totalCredits =
    (user?.credits ?? 0) + (user && !user.has_used_free_credit ? 1 : 0);
  const isAdmin = !!user?.is_admin;

  return (
    <nav
      data-testid="primary-nav"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A1628]/80 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group shrink-0"
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={appName}
              className="h-9 w-9 rounded-sm object-contain"
            />
          ) : (
            <span className="h-9 w-9 rounded-sm bg-gradient-to-br from-[#C9A961] to-[#E0B64B] flex items-center justify-center font-display font-black text-[#0A1628] text-base">
              {initial}
            </span>
          )}
          <span className="flex flex-col leading-tight">
            <span className="font-wordmark font-semibold tracking-tight text-[17px] text-[#F5F1E8]">
              {appName}
            </span>
            {subtitle && (
              <span className="hidden sm:block text-[10px] text-[#9AA5B5] tracking-wide">
                {subtitle}
              </span>
            )}
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-7 text-sm font-body text-[#9AA5B5]">
          {location.pathname !== "/" && (
            <Link to="/" className="hover:text-white transition-colors" data-testid="nav-home">
              Home
            </Link>
          )}
          {user && (
            <>
              <Link
                to="/dashboard"
                className="hover:text-white transition-colors"
                data-testid="nav-dashboard"
              >
                My Offers
              </Link>
              <Link
                to="/new"
                className="hover:text-white transition-colors"
                data-testid="nav-new-offer"
              >
                New Offer
              </Link>
            </>
          )}
          <Link
            to="/pricing"
            className={
              "hover:text-white transition-colors" +
              (showPricing ? "" : " hidden")
            }
            data-testid="nav-pricing"
          >
            Pricing
          </Link>
        </div>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              {showPricing ? (
                <Link
                  to="/pricing"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-mono text-[#C9A961] hover:bg-white/10 transition-colors"
                  data-testid="nav-credits-badge"
                  title={isAdmin ? "Admin — unlimited credits" : "Credit balance"}
                >
                  <Lightning weight="fill" className="h-3.5 w-3.5" />
                  {isAdmin ? (
                    <>
                      <span className="font-bold">∞</span>
                      <span className="text-[#9AA5B5]">unlimited</span>
                    </>
                  ) : (
                    <>
                      <span className="font-bold">{totalCredits}</span>
                      <span className="text-[#9AA5B5]">credits</span>
                    </>
                  )}
                </Link>
              ) : (
                <div
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-mono text-[#C9A961]"
                  data-testid="nav-credits-badge"
                  title={isAdmin ? "Admin — unlimited credits" : "Credit balance"}
                >
                  <Lightning weight="fill" className="h-3.5 w-3.5" />
                  {isAdmin ? (
                    <>
                      <span className="font-bold">∞</span>
                      <span className="text-[#9AA5B5]">unlimited</span>
                    </>
                  ) : (
                    <>
                      <span className="font-bold">{totalCredits}</span>
                      <span className="text-[#9AA5B5]">credits</span>
                    </>
                  )}
                </div>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="hidden md:flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-white/5 transition-colors"
                    data-testid="nav-user-menu"
                  >
                    {user.picture && (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="h-8 w-8 rounded-full border border-white/10"
                      />
                    )}
                    <span className="text-sm text-[#F5F1E8] hidden lg:block">
                      {user.name.split(" ")[0]}
                    </span>
                    <CaretDown className="h-3 w-3 text-[#9AA5B5]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-[#0F1F38] border-white/10 text-[#F5F1E8] min-w-[180px]"
                >
                  {user.is_admin && (
                    <DropdownMenuItem
                      onClick={() => navigate("/admin")}
                      className="cursor-pointer focus:bg-white/5 focus:text-[#C9A961]"
                      data-testid="nav-menu-admin"
                    >
                      Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => navigate("/settings")}
                    className="cursor-pointer focus:bg-white/5 focus:text-white"
                    data-testid="nav-menu-settings"
                  >
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/workspaces")}
                    className="cursor-pointer focus:bg-white/5 focus:text-white"
                    data-testid="nav-menu-workspaces"
                  >
                    Workspaces
                  </DropdownMenuItem>
                  {showPricing && (
                    <DropdownMenuItem
                      onClick={() => navigate("/pricing")}
                      className="cursor-pointer focus:bg-white/5 focus:text-white"
                      data-testid="nav-menu-pricing"
                    >
                      Buy credits
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-[#9AA5B5] focus:bg-white/5 focus:text-[#ef4444]"
                    data-testid="nav-menu-logout"
                  >
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="md:hidden text-[#9AA5B5] hover:text-white hover:bg-white/5"
                data-testid="nav-logout-btn"
              >
                Logout
              </Button>
            </>
          ) : (
            <Button
              onClick={() => navigate("/login")}
              className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm px-6"
              data-testid="nav-login-btn"
            >
              Sign in
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
}
